import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import i18n from "./i18n";
import { ToastContainer } from "react-toastify";
import { LayoutProvider } from "./context/LayoutContext";
import "react-toastify/dist/ReactToastify.css";
import './App.css';
import LoginPage from './components/LoginPage';
import Navbar from './components/Navbar';
import ReservationsList from './components/ReservationsList';
import ReservationForm from './components/ReservationForm';
import BBQ from './components/BBQ';
import KitchenView from './components/Kitchen';
import Service from './components/Service';
import Settings from './components/Settings';
import Users from "./components/Users";
import Layout from "./components/Layout";
import Reports from "./components/Reports";
import Scheduler from "./components/Scheduler";
import Schedule from "./components/Schedule";
import Billing from "./components/Billing";
import MenuManager from './components/MenuManager';
import OrdersReportsPage from './components/OrdersReportsPage';
import { loadAppSettings } from './utils/appSettings';
import OrdersPage from './components/OrdersPage';
import StationScreen from './components/StationScreen';
import OrderPublicPage from './components/OrderPublicPage';
import BuzzSignup from './buzz/BuzzSignup';
import BuzzBoard from './buzz/BuzzBoard';
import BuzzTables from './buzz/BuzzTables';
import BuzzTeam from './buzz/BuzzTeam';
import BuzzSettings from './buzz/BuzzSettings';
import BuzzBilling from './buzz/BuzzBilling';
import BuzzReports from './buzz/BuzzReports';
import BuzzZones from './buzz/BuzzZones';
import BuzzLanding from './buzz/BuzzLanding';
import AdminOverview from './admin/AdminOverview';
import AdminCompanies from './admin/AdminCompanies';
import AdminProducts from './admin/AdminProducts';
import AdminPayments from './admin/AdminPayments';
import AdminInvoices from './admin/AdminInvoices';
import AdminSystem from './admin/AdminSystem';
import AdminLeads from './admin/AdminLeads';
import ResetPasswordPage from './components/ResetPasswordPage';
import InvitePage from './components/InvitePage';
import LegalAcceptGate from './components/LegalAcceptGate';
import IncomingCallBanner from './components/IncomingCallBanner';
import Clients from "./components/Clients";
import MarketingHome from "./MarketingHome";
import DemoPlayer from "./DemoPlayer";
import { getMessagingSafe, getToken } from "./firebaseConfig";
import { onMessage } from "firebase/messaging";
import axios from 'axios';
import config from './config';
import NotificationPrompt from "./components/NotificationPrompt";
import { shouldShowPrompt } from "./notificationsClient";
import AddToHomeScreenGate from "./components/AddToHomeScreenGate";
import { hasNotificationAPI } from "./utils/notifySafe";
import { ResponsiveProvider } from "./Responsive";
import { useTranslation } from "react-i18next";
import BuzzerFeedback from "./components/BuzzerFeedback";
import VivaReturnPage from "./components/VivaReturnPage";

// Δυναμικό custom πόστο (8/8/2026): /station/<skey> → generic StationScreen
const DynamicStationRoute = () => {
  const { skey } = useParams();
  return <StationScreen station={skey} />;
};

axios.defaults.withCredentials = true;

// Single global redirect guard (prevents loops)
let isRedirecting401x = false;
const shouldThrottleRedirect = (key = 'lastAuthRedirectTs', ms = 8000) => {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(key) || 0);
  if (now - last < ms) return true;
  sessionStorage.setItem(key, String(now));
  return false;
};

const redirectToLogin = (reason) => {
const tr = i18n.t.bind(i18n);
  if (isRedirecting401x || shouldThrottleRedirect()) return;
  isRedirecting401x = true;
  const stored = localStorage.getItem('user');
  let company = '';
  try { company = JSON.parse(stored)?._company_name || ''; } catch {}
  localStorage.removeItem('user');
  const qs = reason ? tr('?reason={{var1}}', { var1: encodeURIComponent(reason) }) : '';
  const target = company ? tr('/login/{{company}}{{qs}}', { company, qs }) : tr('/login{{qs}}', { qs });
  window.location.assign(target);
};

axios.defaults.withCredentials = true;

const AX_KEY = "__ESTIASYNC_AXIOS_INTERCEPTORS__";
if (typeof window !== "undefined" && !window[AX_KEY]) {
  window[AX_KEY] = true;

  axios.interceptors.response.use(
    (r) => r,
    (err) => {
  
      const status = err?.response?.status;
      const url = err?.config?.url || '';
      const onLoginPage = window.location.pathname.startsWith('/login');
      const isAuthEndpoint = /\/users\/(login|session)\b/.test(url);
      const networkDown = !err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
  
      // Never redirect during the login/session flow or when already on login
      if (onLoginPage || isAuthEndpoint) return Promise.reject(err);
  
      if (status === 401) {
        redirectToLogin('expired');
      } else if (status === 502 || status === 503 || status === 504 || networkDown) {
        // Gateway / server unavailable / offline
        redirectToLogin('api-down');
      }
      return Promise.reject(err);
    }
  );
  
  axios.interceptors.response.use(r => r, async (err) => {
    const status = err?.response?.status;
    const msg = (err?.response?.data && (err.response.data.error || err.response.data.message)) || '';
    if (status === 403 && /csrf/i.test(String(msg))) {
      try {
        const { data } = await axios.get(`${config.API_BASE_URL}/csrf-token`, { withCredentials: true });
        axios.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
        sessionStorage.setItem('csrfToken', data.csrfToken);
        err.config.headers = err.config.headers || {};
        err.config.headers['X-CSRF-Token'] = data.csrfToken;
        return axios(err.config); // retry once
      } catch {}
    }
    return Promise.reject(err);
  });
  
}

// Remember a “pending” completion if user isn’t logged in yet
function getPendingActionId() {
  return sessionStorage.getItem("pendingCompleteActionId");
}

function setPendingActionId(id) {
  if (id) sessionStorage.setItem("pendingCompleteActionId", id);
  else sessionStorage.removeItem("pendingCompleteActionId");
}

const toI18nLang = (dbLang) =>
  String(dbLang || "EN").toUpperCase() === "EL" ? "el" : "en";

const toDbLang = (i18nLang) =>
  String(i18nLang || "en").toLowerCase().startsWith("el") ? "EL" : "EN";

const App = () => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [allowBrowserUse, setAllowBrowserUse] = useState(() => sessionStorage.getItem("allowBrowserUse") === "1");
  const [installedAck, setInstalledAck]     = useState(() => sessionStorage.getItem("installedAck") === "1");
  const [planPerms, setPlanPerms] = useState(null);
  const [langTick, setLangTick] = useState(0);
  
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handler = () => setLangTick((x) => x + 1);
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, []);

  const location = useLocation(); // Use useLocation to get the current route
  
  const [tourActive, setTourActive] = useState(() => {
    const fromQuery = new URLSearchParams(location.search).get("tour") === "1";
    if (fromQuery) sessionStorage.setItem("bbqsync_tour", "1");
    return fromQuery || sessionStorage.getItem("bbqsync_tour") === "1";
  });
  useEffect(() => {
    const fromQuery = new URLSearchParams(location.search).get("tour") === "1";
    if (fromQuery) {
      sessionStorage.setItem("bbqsync_tour", "1");
      if (!tourActive) setTourActive(true);
    }
  }, [location.search, tourActive]);

  // iOS / PWA detection helpers (used in App.js)
  function isIOS() {
    return /iPhone|iPad|iPod/.test(navigator.userAgent);
  }
  
  function isStandalone() {
    // iOS supports both the Display Mode API and the legacy `navigator.standalone`
    return (
      (typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches) ||
      (typeof navigator !== "undefined" && navigator.standalone === true)
    );
  }

  const handleLanguageChange = async (lng) => {
    const i18nLng = toI18nLang(lng);
    const dbLang = toDbLang(i18nLng);
  
    // 1) Instant UI update
    i18n.changeLanguage(i18nLng);
  
    // 2) Update local user + storage
    if (user) {
      const updated = { ...user, language: dbLang };
      setUser(updated);
      localStorage.setItem("user", JSON.stringify(updated));
    }
  
    // 3) Persist to DB
    try {
      await axios.put(
        `${config.API_BASE_URL}/users/language`,
        { language: dbLang },
        { withCredentials: true }
      );
    } catch (err) {
      console.error("Language save failed:", err);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const cached = localStorage.getItem("user");
        if (cached) {
          const u = JSON.parse(cached);
          setUser(u);
          i18n.changeLanguage(toI18nLang(u.language));
        } else {
          const res = await axios.get(`${config.API_BASE_URL}/users/session`, { withCredentials: true });
          if (res.data.active) handleLogin(res.data.user);
          else setUser(null);
        }
      } catch { setUser(null); }
      finally { setReady(true); }
    })();
  }, []);
  
  useEffect(() => {
    if (!user?.language) return;
  
    const desired = toI18nLang(user.language);
    const current = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  
    if (current !== desired) {
      i18n.changeLanguage(desired);
    }
  }, [user?.language, i18n]);  
      
  useEffect(() => {
    (async () => {
      // if you already check session here, keep that logic
      const saved = sessionStorage.getItem('csrfToken');
      if (saved) {
        axios.defaults.headers.common['X-CSRF-Token'] = saved;
      } else {
        try {
          const { data } = await axios.get(`${config.API_BASE_URL}/csrf-token`, { withCredentials: true });
          axios.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
          sessionStorage.setItem('csrfToken', data.csrfToken);
        } catch {}
      }
    })();
  }, []);      
      
  // Decide whether to show the enable-notifications prompt
  useEffect(() => {
    if (ready && user?.push_notifications) {
      setShowPrompt(shouldShowPrompt());
    } else {
      setShowPrompt(false);
    }
  }, [ready, user]);
      
  // Call this from a button (e.g., in Navbar or Settings)
  async function enablePush() {
    try {
    
      if (!user?.push_notifications) return;
 
      // If the Notification API is not available, don’t try to request permission
      if (!hasNotificationAPI()) {
        const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
        const inPWA = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (navigator.standalone === true);
        if (isIOS && !inPWA) {
          alert("On iPhone, add estiasync to your Home Screen first (Share → Add to Home Screen), then open it and tap Enable again.");
        } else {
          alert("Notifications aren’t supported in this browser context.");
        }
        return;
      }      

      console.log("Requesting permission...");
      const permission = await window.Notification.requestPermission();
      if (permission === "granted") {
          console.log("Notification permission granted.");
          try {
              const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
              console.log("Service Worker Registered:", registration);
              
              await navigator.serviceWorker.ready;
              
              const msg = await getMessagingSafe();
              const token = await getToken(msg, {
                  vapidKey: "BOtio-4U8ct6Q36KkxvjXY1wpzEDnYPpe-SBzJbqckTbnh_bLy5ue4RsroVxjQSR9xmntjCEnLWxt8YPMX6Le8o",
                  serviceWorkerRegistration: registration,
              });
              console.log("FCM Token:", token);
              try {
                await axios.post(`${config.API_BASE_URL}/users/token`, { token }, { withCredentials: true });
                console.log("Token saved successfully");      
                setShowPrompt(false);        
              } catch (error) {
                  console.error("Error saving token:", error);
              }  
          } catch (error) {
              console.error("Error getting FCM token", error);
          }
      } else {
          console.log("Notification permission denied.");
      }
    } catch (e) { console.error(e); }
  }
        
  // Only attach foreground listener if the user opted in AND permission is granted
  useEffect(() => {
    if (!user?.push_notifications) return;
    if (!hasNotificationAPI() || window.Notification.permission !== "granted") return;
    let unsub = null;
    (async () => {
      try {
        const msg = await getMessagingSafe();
        unsub = onMessage(msg, (payload) => {
          console.log("Foreground push notification received:", payload);
            const d = payload?.data || {};
            // Buzz control μηνύματα (dismiss/update): στο foreground ο χρήστης βλέπει
            // ήδη το board — μην εμφανίζεις επιπλέον notification
            if (d.buzzAction === "dismiss" || d.silent === "1") return;
            const title = d.title || payload?.notification?.title || "estiasync.com";
            const body  = d.body  || payload?.notification?.body  || "";
            new window.Notification(title, {
            body,
            icon: "/bbq_service_icon.ico",
            vibrate: [200, 100, 200],
            tag: "push-notification"
          });
        });
      } catch (e) { /* messaging not supported – ignore */ }
    })();
    return () => { if (typeof unsub === 'function') try { unsub(); } catch {} };
  }, [user]);

  // 1) On load, if URL has ?completeAction=ID, either complete now (if logged in),
  //    or remember it for after login.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("completeAction");
    if (id) {
      if (user) {
        axios.put(`${config.API_BASE_URL}/actions/${encodeURIComponent(id)}/complete`, null, { withCredentials: true })
          .catch(() => {}) // ignore errors
          .finally(() => {
            params.delete("completeAction");
            const newUrl = window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash;
            window.history.replaceState(null, "", newUrl);
          });
      } else {
        setPendingActionId(id);
      }
    }
  }, [user]);

  // 2) If we stored a pending action and the user just logged in, complete it now.
  useEffect(() => {
    if (!user) return;
    const pending = getPendingActionId();
    if (!pending) return;
    axios.put(`${config.API_BASE_URL}/actions/${encodeURIComponent(pending)}/complete`, null, { withCredentials: true })
      .finally(() => setPendingActionId(null));
  }, [user]);


  const handleLogin = async  (userData) => {
    const cleanedUser = userData;
    cleanedUser.view_admin= !!cleanedUser.view_admin?.data?.[0];
    cleanedUser.view_reservations_list= !!cleanedUser.view_reservations_list?.data?.[0];
    cleanedUser.view_reservation_form= !!cleanedUser.view_reservation_form?.data?.[0];
    cleanedUser.view_bbq= !!cleanedUser.view_bbq?.data?.[0];
    cleanedUser.view_kitchen= !!cleanedUser.view_kitchen?.data?.[0];
    cleanedUser.view_service= !!cleanedUser.view_service?.data?.[0];
    cleanedUser.view_users= !!cleanedUser.view_users?.data?.[0];
    cleanedUser.view_settings= !!cleanedUser.view_settings?.data?.[0];
    cleanedUser.view_tables= !!cleanedUser.view_tables?.data?.[0];
    cleanedUser.view_reports= !!cleanedUser.view_reports?.data?.[0];
    cleanedUser.view_scheduler= !!cleanedUser.view_scheduler?.data?.[0];
    cleanedUser.view_schedule= !!cleanedUser.view_schedule?.data?.[0];
    cleanedUser.view_billing= !!cleanedUser.view_billing?.data?.[0];
    cleanedUser.view_clients= !!cleanedUser.view_clients?.data?.[0];
    cleanedUser.view_orders = !!cleanedUser.view_orders?.data?.[0];
    cleanedUser.view_bar = !!cleanedUser.view_bar?.data?.[0];
    cleanedUser.view_salads = !!cleanedUser.view_salads?.data?.[0];
    cleanedUser.view_desserts = !!cleanedUser.view_desserts?.data?.[0];
    
    cleanedUser.at_management= !!cleanedUser.at_management?.data?.[0];
    cleanedUser.at_bbq= !!cleanedUser.at_bbq?.data?.[0];
    cleanedUser.at_kitchen= !!cleanedUser.at_kitchen?.data?.[0];
    cleanedUser.at_cleaning= !!cleanedUser.at_cleaning?.data?.[0];
    cleanedUser.at_service= !!cleanedUser.at_service?.data?.[0];
    cleanedUser.push_notifications= !!cleanedUser.push_notifications?.data?.[0];
    
    await i18n.changeLanguage(toI18nLang(cleanedUser.language));
    
    localStorage.setItem("user", JSON.stringify(cleanedUser)); // Store user in localStorage
    setUser(cleanedUser);
    setReady(true);        
  };

  const handleLogout = async () => {
    const company = user?._company_name || ""; // stash before we clear state
    try {
      await axios.post(`${config.API_BASE_URL}/users/logout`, null, { withCredentials: true });
    } catch {} // even if request fails, proceed to clear client state
  
    setUser(null);
    localStorage.removeItem("user");
    sessionStorage.removeItem("csrfToken"); // optional: drop any stale CSRF tokens
  
    // Hard redirect to the company login page to avoid any stale in-memory state
    window.location.assign(company ? `/login/${company}` : `/login`);
  };

  const refreshPlanPerms = useCallback(async () => {
    if (!user?.id) {
      setPlanPerms(null);
      return;
    }

    try {
      const { data } = await axios.get(
        `${config.API_BASE_URL}/users/plan-permissions`,
        { withCredentials: true }
      );
      const { active, permissions } = data || {};
      if (!active || !permissions) {
        setPlanPerms({});
        return;
      }

      const normalize = (v) => {
        if (!v) return false;
        if (v.data) return !!v.data[0]; // BIT(1)
        return !!v;
      };

      setPlanPerms({
        view_reservations_list: normalize(permissions.view_reservations_list),
        view_reservation_form:  normalize(permissions.view_reservation_form),
        view_bbq:               normalize(permissions.view_bbq),
        view_kitchen:           normalize(permissions.view_kitchen),
        view_service:           normalize(permissions.view_service),
        view_scheduler:         normalize(permissions.view_scheduler),
        view_schedule:          normalize(permissions.view_schedule),
        view_reports:           normalize(permissions.view_reports),
        view_clients:           normalize(permissions.view_clients),
        view_orders:            normalize(permissions.view_orders),
        view_bar:               normalize(permissions.view_bar),
        view_salads:            normalize(permissions.view_salads),
        view_desserts:          normalize(permissions.view_desserts),
        view_settings:          normalize(permissions.view_settings),
        view_tables:            normalize(permissions.view_tables),
        view_users:             normalize(permissions.view_users),
      });
    } catch (e) {
      console.error("plan-permissions error", e);
      // If error, skip plan enforcement on frontend
      setPlanPerms(null);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshPlanPerms();
  }, [refreshPlanPerms]);

  useEffect(() => {
    if (!user?.id) return;

    const onFocus = () => refreshPlanPerms();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshPlanPerms();
    };
    const onManualRefresh = () => refreshPlanPerms();

    window.addEventListener('focus', onFocus);
    window.addEventListener('estiasync:plan-refresh', onManualRefresh);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('estiasync:plan-refresh', onManualRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id, refreshPlanPerms]);

  useEffect(() => {
    if (!user?.id) return;
    if (location.pathname === '/billing' || location.pathname.startsWith('/payments/viva/')) {
      refreshPlanPerms();
    }
  }, [user?.id, location.pathname, refreshPlanPerms]);

  const inPlan = (perm) => {
    if (!planPerms) return true;      // before load or on error: don't block
    return !!planPerms[perm];        // plan says yes/no
  };
  
  useEffect(() => { if (user) loadAppSettings(); }, [user]);

  const canAccess = (u, perm) => {
    if (!u) return false;
    const hasUserPerm = u.view_admin || u[perm];
    return hasUserPerm && inPlan(perm);
  };

  const canAccessBilling = (u) => {
    if (!u) return false;
    return !!(u.view_admin || u.view_billing);
  };

  // BIT flags φτάνουν από το API ως {type:'Buffer',data:[0|1]} — πάντα truthy·
  // για το Buzz (που δεν περνά από plan) χρειάζεται πραγματικός έλεγχος του bit.
  const bitOn = (v) =>
    v === 1 || v === true || v === '1' ||
    (v && typeof v === 'object' && Array.isArray(v.data) && v.data[0] === 1);

  const defaultPath = (u) =>
    // Buzz-only λογαριασμοί (view_buzz χωρίς κανένα ενεργό plan) προσγειώνονται στο board
    (bitOn(u?.view_buzz) && planPerms !== null && !Object.values(planPerms || {}).some(Boolean) && '/buzz/board') ||
    // same priority as before, but now respecting plan
    (((canAccess(u, "view_reservations_list") || canAccess(u, "view_reservation_form")) && '/reservations-list')) ||
    (canAccess(u, "view_schedule")   && '/schedule')  ||
    (canAccess(u, "view_bbq")        && '/bbq')       ||
    (canAccess(u, "view_kitchen")    && '/kitchen')   ||
    (canAccess(u, "view_service")    && '/service')   ||
    (canAccess(u, "view_orders")     && '/orders')    ||
    (canAccess(u, "view_scheduler")  && '/scheduler') ||
    (canAccess(u, "view_reports")    && '/reports')   ||
    (canAccess(u, "view_settings")   && '/settings')  ||
    (canAccess(u, "view_tables")     && '/tables')    ||
    (canAccess(u, "view_clients")    && '/clients')   ||
    (canAccess(u, "view_users")      && '/users')     ||
    (canAccessBilling(u)             && '/billing')   ||
    // Super-admin χωρίς κανένα άλλο δικαίωμα (προσωπικός εταιρικός λογαριασμός) → /admin
    (bitOn(u?.view_superadmin)       && '/admin/overview') ||
    '/login';

  // Handlers for the gate
  const handleContinueInBrowser = () => {
    sessionStorage.setItem("allowBrowserUse", "1");
    setAllowBrowserUse(true);
  };
  
  const handleInstalled = () => {
    // Close the gate for this Safari session and tell the user what to do next.
    sessionStorage.setItem("installedAck", "1");
    setInstalledAck(true);
    try {
      alert("Great! Now close Safari and open Estiasync app from your Home-Screen icon to finish.");
    } catch {}
    // (If they really *are* already in PWA, the gate would not be shown anyway.)
  };
  

  // Το body παίρνει padding για το navbar μόνο όταν το navbar όντως εμφανίζεται.
  // Στη σελίδα marketing (/home) δεν δείχνουμε το app navbar ακόμα κι αν ο
  // χρήστης είναι συνδεδεμένος — εκεί έχει το δικό της header.
  const navbarVisible =
    !location.pathname.startsWith('/login') &&
    location.pathname !== '/home' &&
    !!user;
  useEffect(() => {
    document.body.classList.toggle('has-navbar', navbarVisible);
    return () => document.body.classList.remove('has-navbar');
  }, [navbarVisible]);

  return ready && (
    <ResponsiveProvider>
      <LayoutProvider user={user}>
        {(location.pathname === '/bbq' || location.pathname === '/kitchen' || location.pathname === '/settings' || location.pathname.startsWith('/orders') || location.pathname.startsWith('/station')) && <ToastContainer/>}
        {navbarVisible && (
          <Navbar user={user} onLogout={handleLogout} planPerms={planPerms} onLanguageChange={handleLanguageChange} />
        )}

        {/* Click-wrap: αποδοχή Όρων/Σύμβασης από admin πελάτη όταν εκκρεμεί */}
        {user && navbarVisible && <LegalAcceptGate user={user} />}

        {/* Αναγνώριση κλήσης (Yealink): «Καλεί τώρα» για όσους παίρνουν κρατήσεις */}
        {user && navbarVisible && <IncomingCallBanner user={user} />}

        {/* Impersonation banner: ο super-admin βλέπει περιβάλλον πελάτη — πάντα ορατή έξοδος.
            z-index 1500: πάνω από navbar (600) και modals (1000), κάτω από mobile navbar (9999). */}
        {user?._impersonating && (
          <div
            style={{
              position: 'fixed', bottom: 12, left: '50%', transform: 'translateX(-50%)',
              zIndex: 1500, background: '#111827', color: '#fff', borderRadius: 10,
              padding: '8px 14px', display: 'flex', gap: 10, alignItems: 'center',
              boxShadow: '0 4px 14px rgba(0,0,0,.35)', fontSize: 13.5, maxWidth: '92vw',
            }}
          >
            <span>🔓 {i18n.t("Customer environment")}: <strong>{user._impersonating.companyName}</strong></span>
            <button
              onClick={async () => {
                try {
                  const r = await axios.post(`${config.API_BASE_URL}/admin/impersonate/stop`);
                  localStorage.setItem('user', JSON.stringify(r.data));
                  window.location.assign('/admin/overview');
                } catch {
                  // αν η session χάθηκε, καθάρισε και πήγαινε για login
                  localStorage.removeItem('user');
                  window.location.assign('/login');
                }
              }}
              style={{
                background: '#fff', color: '#111827', border: 'none', borderRadius: 8,
                padding: '5px 10px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              {i18n.t("Back to administration")}
            </button>
          </div>
        )}
        
        {/* iOS + not-installed => show gate AFTER login unless user chose to continue in browser */}
        {user && isIOS() && !isStandalone() && !allowBrowserUse && !installedAck && (
          <AddToHomeScreenGate
            onContinueInBrowser={handleContinueInBrowser}
            onInstalled={handleInstalled}
          />
        )}      
        
        {showPrompt && user?.push_notifications && !location.pathname.startsWith('/login') && (
          <NotificationPrompt
            onEnabled={() => setShowPrompt(false)}
            onDismiss={() => setShowPrompt(false)}
            onEnable={enablePush}
          />
        )}      
        
        <Routes>
          <Route
            path="/"
            element={
              user
                ? <Navigate to={defaultPath(user)} replace />
                : <MarketingHome />
            }
          />
          <Route
            path="/home"
            element={<MarketingHome />}
          />          
          {canAccess(user, "view_reservation_form")     && <Route path="/reservation-form"   element={user ? <ReservationForm /> : <Navigate to="/" replace />} />}
          {canAccess(user, "view_service")              && <Route path="/service"            element={user ? <Service />         : <Navigate to="/" replace />} />}
          {canAccess(user, "view_reports")              && <Route path="/reports"            element={user ? <Reports />         : <Navigate to="/" replace />} />}
          {canAccess(user, "view_tables")               && <Route path="/tables"             element={user ? <Layout />          : <Navigate to="/" replace />} />}
          {canAccess(user, "view_reservations_list")    && <Route path="/reservations-list"  element={user ? <ReservationsList />: <Navigate to="/" replace />} />}
          {canAccess(user, "view_bbq")                  && <Route path="/bbq"                element={user ? <BBQ />             : <Navigate to="/" replace />} />}
          {canAccess(user, "view_kitchen")              && <Route path="/kitchen"            element={user ? <KitchenView />     : <Navigate to="/" replace />} />}
          {canAccess(user, "view_settings")             && <Route path="/settings"           element={user ? <Settings />        : <Navigate to="/" replace />} />}
          {canAccess(user, "view_users")                && <Route path="/users"              element={user ? <Users />           : <Navigate to="/" replace />} />}
          {canAccess(user, "view_scheduler")            && <Route path="/scheduler"          element={user ? <Scheduler />       : <Navigate to="/" replace />} />}
          {canAccess(user, "view_schedule")             && <Route path="/schedule"           element={user ? <Schedule />        : <Navigate to="/" replace />} />}
          {canAccessBilling(user)                        && <Route path="/billing"            element={user ? <Billing />         : <Navigate to="/" replace />} />}
          {canAccess(user, "view_clients")              && <Route path="/clients"            element={user ? <Clients />         : <Navigate to="/" replace />} />}
          {canAccess(user, "view_orders")               && <Route path="/orders/menu"        element={user ? <MenuManager />     : <Navigate to="/" replace />} />}
          {canAccess(user, "view_orders")               && <Route path="/orders/reports"     element={user ? <OrdersReportsPage /> : <Navigate to="/" replace />} />}
          {canAccess(user, "view_orders")               && <Route path="/orders"             element={user ? <OrdersPage />      : <Navigate to="/" replace />} />}
          {canAccess(user, "view_bar")                  && <Route path="/bar"                element={user ? <StationScreen station="bar" />      : <Navigate to="/" replace />} />}
          {canAccess(user, "view_salads")               && <Route path="/salads"             element={user ? <StationScreen station="salads" />   : <Navigate to="/" replace />} />}
          {canAccess(user, "view_desserts")             && <Route path="/desserts"           element={user ? <StationScreen station="desserts" /> : <Navigate to="/" replace />} />}
          {/* Δυναμικά custom πόστα (8/8/2026): /station/<skey> → generic StationScreen */}
          {canAccess(user, "view_orders")               && <Route path="/station/:skey"      element={user ? <DynamicStationRoute /> : <Navigate to="/" replace />} />}

          {/* EstiaSync Buzz — αυτοτελές standalone προϊόν (ΕΚΤΟΣ plan gating: δεν περνά από _plans) */}
          <Route path="/buzz-qr" element={<BuzzLanding />} />
          <Route path="/buzz-qr/register" element={<BuzzSignup />} />
          <Route path="/buzz" element={user ? <Navigate to="/buzz/board" replace /> : <Navigate to="/login" replace />} />
          <Route path="/buzz/board" element={user ? <BuzzBoard /> : <Navigate to="/login" replace />} />
          <Route path="/buzz/tables" element={user ? <BuzzTables /> : <Navigate to="/login" replace />} />
          <Route path="/buzz/team" element={user ? <BuzzTeam /> : <Navigate to="/login" replace />} />
          <Route path="/buzz/settings" element={user ? <BuzzSettings /> : <Navigate to="/login" replace />} />
          <Route path="/buzz/billing" element={user ? <BuzzBilling /> : <Navigate to="/login" replace />} />
          <Route path="/buzz/reports" element={user ? <BuzzReports /> : <Navigate to="/login" replace />} />
          <Route path="/buzz/zones" element={user ? <BuzzZones /> : <Navigate to="/login" replace />} />

          {/* /admin — εταιρικό εποπτικό περιβάλλον (super-admin, cross-tenant). Εκτός plan gating·
              η πραγματική εξουσιοδότηση γίνεται server-side (flag + IP allowlist), το AdminShell
              δείχνει "δεν έχεις πρόσβαση" στο 403. */}
          <Route path="/admin" element={user ? <Navigate to="/admin/overview" replace /> : <Navigate to="/login" replace />} />
          <Route path="/admin/overview" element={user ? <AdminOverview /> : <Navigate to="/login" replace />} />
          <Route path="/admin/companies" element={user ? <AdminCompanies /> : <Navigate to="/login" replace />} />
          <Route path="/admin/products" element={user ? <AdminProducts /> : <Navigate to="/login" replace />} />
          <Route path="/admin/payments" element={user ? <AdminPayments /> : <Navigate to="/login" replace />} />
          <Route path="/admin/invoices" element={user ? <AdminInvoices /> : <Navigate to="/login" replace />} />
          <Route path="/admin/system" element={user ? <AdminSystem /> : <Navigate to="/login" replace />} />
          <Route path="/admin/leads" element={user ? <AdminLeads /> : <Navigate to="/login" replace />} />

          <Route path="/forgot-password" element={<ResetPasswordPage mode="forgot" />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage mode="reset" />} />
          <Route path="/invite/:token" element={<InvitePage />} />

          <Route path="/login/:company" element={<LoginPage onLogin={handleLogin} />} />
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          <Route path="/order/:token" element={<OrderPublicPage />} />
          <Route path="/buzzer/:id/:sig" element={<BuzzerFeedback />} />
          
          <Route path="/payments/viva/success" element={<VivaReturnPage mode="success" />} />
          <Route path="/payments/viva/fail" element={<VivaReturnPage mode="fail" />} />
          
          <Route
            path="*"
            element={user ? <Navigate to={defaultPath(user)} replace /> : <Navigate to="/" replace />}
          />
        </Routes>
        {tourActive && <DemoPlayer onDone={() => {
          sessionStorage.removeItem("bbqsync_tour");
          setTourActive(false);
        }} />}
      </LayoutProvider>
    </ResponsiveProvider>
  );
};

export default App;
